import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ListItem } from '@components/ListItem';
import { Input } from '@components/Input';
import { IconSearch } from '@components/icons';
import styles from './MenuSelect.module.scss';

export interface MenuSelectItem {
  /** Unique identifier for the item */
  value: string;
  /** Primary text displayed */
  label: string;
  /** Optional secondary text */
  subtitle?: string;
  /** Whether the item is disabled */
  disabled?: boolean;
  /**
   * Optional group label. When at least one item has a group, the menu
   * renders headers and partitions items by group. Items without a group
   * still render (with no header). Group order follows first-appearance
   * order in the items array.
   */
  group?: string;
}

export interface MenuSelectProps {
  /** Array of selectable items */
  items: MenuSelectItem[];
  /** Currently selected value (single-select mode) */
  value?: string | null;
  /** Callback when selection changes (single-select mode) */
  onChange?: (value: string) => void;
  /** Enable multi-select mode */
  multiSelect?: boolean;
  /** Array of selected values (multi-select mode) */
  selectedValues?: string[];
  /** Callback when selection changes (multi-select mode) */
  onSelectionChange?: (values: string[]) => void;
  /** Maximum height before scrolling (default: 240px) */
  maxHeight?: number;
  /** Enable type-to-filter input at the top of the menu */
  filterable?: boolean;
  /** Placeholder text for filter input (defaults to i18n key) */
  filterPlaceholder?: string;
  /** Additional CSS classes */
  className?: string;
}

export const MenuSelect = ({
  items,
  value,
  onChange,
  multiSelect = false,
  selectedValues,
  onSelectionChange,
  maxHeight = 240,
  filterable = false,
  filterPlaceholder,
  className = '',
}: MenuSelectProps) => {
  const { t } = useTranslation();
  const [filterText, setFilterText] = useState('');

  // In multi-select mode, inject synthetic items for selected values missing from items list
  const allItems = useMemo(() => {
    if (!multiSelect || !selectedValues?.length) return items;
    const itemValues = new Set(items.map((item) => item.value));
    const missing: MenuSelectItem[] = selectedValues
      .filter((v) => !itemValues.has(v))
      .map((v) => ({ value: v, label: v }));
    return missing.length > 0 ? [...missing, ...items] : items;
  }, [items, multiSelect, selectedValues]);

  const hasGroups = useMemo(() => items.some((i) => i.group), [items]);

  // Items after filter input has been applied (no float-to-top, no grouping yet)
  const searchedItems = useMemo(() => {
    const source = multiSelect ? allItems : items;
    if (!filterable || !filterText.trim()) return source;
    const query = filterText.toLowerCase().trim();
    return source.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        (item.subtitle && item.subtitle.toLowerCase().includes(query)),
    );
  }, [items, allItems, filterText, filterable, multiSelect]);

  // Flat-mode list (legacy): items after filter, with selected floated to top in multi-select
  const filteredItems = useMemo(() => {
    if (multiSelect && selectedValues?.length) {
      const selected = new Set(selectedValues);
      const selectedItems = searchedItems.filter((item) => selected.has(item.value));
      const unselectedItems = searchedItems.filter((item) => !selected.has(item.value));
      return [...selectedItems, ...unselectedItems];
    }
    return searchedItems;
  }, [searchedItems, multiSelect, selectedValues]);

  // Grouped-mode rendering: ordered Map<groupLabel, items[]> with a 'Selected'
  // bucket at the top in multi-select mode. Group order follows first-appearance
  // order in the items array. Empty groups (after filter) are omitted.
  const groupedItems = useMemo(() => {
    if (!hasGroups) return null;

    const groups = new Map<string, MenuSelectItem[]>();
    const selectedSet =
      multiSelect && selectedValues?.length ? new Set(selectedValues) : null;
    const selectedLabel = t('common.selected');

    if (selectedSet) {
      const selectedItems = searchedItems.filter((item) => selectedSet.has(item.value));
      if (selectedItems.length > 0) {
        groups.set(selectedLabel, selectedItems);
      }
    }

    for (const item of searchedItems) {
      if (selectedSet?.has(item.value)) continue; // already in 'Selected'
      const g = item.group ?? '';
      const existing = groups.get(g);
      if (existing) {
        existing.push(item);
      } else {
        groups.set(g, [item]);
      }
    }

    return groups;
  }, [hasGroups, searchedItems, multiSelect, selectedValues, t]);

  const containerClassName = [styles.container, className].filter(Boolean).join(' ');

  const handleSelect = (itemValue: string) => {
    if (multiSelect && onSelectionChange) {
      const current = selectedValues ?? [];
      const newValues = current.includes(itemValue)
        ? current.filter((v) => v !== itemValue)
        : [...current, itemValue];
      onSelectionChange(newValues);
    } else if (onChange) {
      onChange(itemValue);
    }
  };

  const placeholder = filterPlaceholder ?? t('common.filterItems');

  return (
    <div className={containerClassName}>
      {filterable && (
        <div className={styles.filterWrapper}>
          <Input
            value={filterText}
            onChange={setFilterText}
            placeholder={placeholder}
            leadingIcon={<IconSearch size={14} />}
            clearable
            size="slim"
          />
        </div>
      )}
      <div
        className={styles.listWrapper}
        style={{ maxHeight }}
        role="listbox"
        aria-label={multiSelect ? 'Select options' : 'Select an option'}
        aria-multiselectable={multiSelect || undefined}
      >
        {groupedItems ? (
          groupedItems.size > 0 ? (
            [...groupedItems.entries()].map(([groupLabel, groupItems]) => (
              <div key={groupLabel || '__ungrouped'} className={styles.group}>
                {groupLabel && (
                  <div className={styles.groupHeader}>{groupLabel}</div>
                )}
                {groupItems.map((item) => (
                  <ListItem
                    key={item.value}
                    title={item.label}
                    subtitle={item.subtitle}
                    selected={
                      multiSelect
                        ? (selectedValues ?? []).includes(item.value)
                        : value === item.value
                    }
                    disabled={item.disabled}
                    onClick={() => !item.disabled && handleSelect(item.value)}
                  />
                ))}
              </div>
            ))
          ) : (
            <div className={styles.noResults}>{t('common.noResults')}</div>
          )
        ) : filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <ListItem
              key={item.value}
              title={item.label}
              subtitle={item.subtitle}
              selected={
                multiSelect
                  ? (selectedValues ?? []).includes(item.value)
                  : value === item.value
              }
              disabled={item.disabled}
              onClick={() => !item.disabled && handleSelect(item.value)}
            />
          ))
        ) : (
          <div className={styles.noResults}>{t('common.noResults')}</div>
        )}
      </div>
    </div>
  );
};
